import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { Recipe } from "../models/Recipe";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import { useTableData } from "../hooks/useTableData";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";

interface RecipesTableProps {
	recipes: Recipe[];
	selectedRecipe: Recipe | null;
	onRowSelected: (recipe: Recipe | null) => void;
	onDelete: (recipe: Recipe) => void;
}

export const RecipesTable = ({
	recipes,
	selectedRecipe,
	onRowSelected,
	onDelete,
}: RecipesTableProps) => {
	const gridRef = useRef<AgGridReact<Recipe>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback((_params: GridReadyEvent<Recipe>) => {}, []);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<Recipe>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<Recipe>(onRowSelected);

	const getRowId = useCallback((params: { data: Recipe }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<Recipe>
				ref={gridRef}
				rowData={recipes}
				columnDefs={columnDefs}
				defaultColDef={listColDef(globalDefaultColDef)}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				getRowStyle={(params) => {
					if (selectedRecipe?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
